interface IUISettings {
    languages?: string[];
    acceptlanguages?: string[];
    defaultlanguage?: string;
}

type PhraseMap = Record<string, string>;

export class LanguageManager {
    public language: string | undefined;
    private phrases: any;
    private groupId: string;
    public ready: boolean;
    public getPhrase(phrase: string, defaultValue?: string) : string {
        if (!this.ready) {
            console.error(`Phrases not yet loaded. Using default phrase: ${defaultValue}`);
            return defaultValue;
        }
        let res = this.phrases[phrase];
        if (res == undefined) {
            res = defaultValue ? defaultValue: phrase;
        }
        return res;
    }

    public getPhraseWithReplacement(phrase: string, replacement: string, defaultValue?: string) : string {
        return this.getPhrase(phrase, defaultValue).replace('{0}', replacement);
    }

    public printableVersion() : LanguageManager{
        const res = Object.assign({},this);
        const num = Object.keys(this.phrases).length;
        if (num > 0) {res.phrases = `Not shown, ${num} phrases loaded.`;}
        return res;
    }

    public constructor(groupId: string) {
        this.phrases = {};
        this.groupId = groupId;
        this.ready = false;
    }

    public async initialize(baseURL: string, translationsURL: string, staging: boolean = false) {
        console.log(`Initializing language manager. Translations URL: ${translationsURL}`);
        try {
            await this.getLanguage(this.groupId, baseURL, staging).then(async language => {
                this.language = language;
                await this.getPhrases(this.groupId, translationsURL, language, staging).then(phrases => {
                    this.phrases = phrases;
                });
            }).then(async ()=>{
                if (Object.keys(this.phrases).length === 0) {
                    console.log(`Translations initialization failed. Retrying.`);
                    await this.getPhrases(this.groupId, translationsURL, this.language, staging).then(phrases => {
                        this.phrases = phrases;
                    });
                }
                this.ready = true;
            });
        }
        catch (error) {
            console.error(`Error obtaining the translations: ${error}. Showing phrases in English.`);
            this.ready = true;//Show english phrases.
        }
    }

    private async nativeFetch(url: string) {
        if (!fetch.toString().includes("native code")) {
            // GD-17420 fetch can be overwritten, get native fetch from iframe
            let frame = document.getElementById("visitor_ui_clean_context") as HTMLIFrameElement;
            if(!frame) {
                frame = document.createElement('iframe');
                frame.id = "visitor_ui_clean_context";
                frame.style.display = "none";
                frame.setAttribute("data-no-cobrowse-content", "1");
                document.body.appendChild(frame);
            }
            return frame.contentWindow ? frame.contentWindow.fetch(url) : window.fetch(url);
        }
        else
            return window.fetch(url);
    }

    /**
     * Get the agent's language, according to the HTML lang attribute, browser accept headers, 
     * and the customer's supported languages
     * Language is selected from the customer's supported languages in this order: 
     * - exact match to HTML lang attribute
     * - exact match to browser language setting
     * - main 2-letter match to HTML lang attribute
     * - main 2-letter match to browser language setting
     * - customer's default language
     * @param {Array} allowedlangs Languages allowed for the group
     * @param {Array} browserlangs Languages configured in browser settings
     * @param {string} defaultlang Default language for the group
     */
    private getAgentLanguage(allowedlangs: string[], browserlangs: string[], defaultlang: string) {
        // This creates a combined list of languages from HTML tag and accept header. We do not want the tag, so that part is commented out.
        //let htmllang = document.documentElement.getAttribute("lang")
        //console.log(`HTML lang: ${htmllang}`);
        //let langs = htmllang ? [htmllang, ...browserlangs] : browserlangs
        const langs = browserlangs.map(lang => lang.toLowerCase())

        // Find a languages from the list desired languages that exactly matches
        // a supported language
        for (let i = 0; i < langs.length; i++)
            if (allowedlangs.includes(langs[i]))
                return langs[i] // exact match

        // No exact match, check for main 2-letter language code match
        for (let i = 0; i < langs.length; i++) {
            const matchinglang = allowedlangs.find(allowedlang => { console.log(`Checking allowed lang: ${allowedlang}`);return allowedlang.substr(0, 2) === langs[i].substr(0, 2) })
            if (matchinglang)
                return matchinglang;
        }

        // No matches, use default
        return defaultlang;
    }

    /** Get visitor UI settings
     * @returns {Promise} Promise which resolves when UI settings have been obtained
     */
    private async getUISettings(groupId: string, baseURL: string, staging: boolean = false) : Promise<IUISettings>{
        console.log("getVisitorUISettings");
        const site = staging ? "staging" : "production";//TODO return something is nativeFetch fails.
        return this.nativeFetch(`${baseURL}/api/CobrowseSettings/VisitorUISettings?groupid=${groupId}&site=${site}&ver=`)
            .then((result: Response) => {
                if (!result.ok)
                    throw new Error(`ERR_SETTINGS: ${result.status} ${result.statusText}`);
                const res = result.json();
                return res;
            })
    }

    /**
     * Get the agent's language, according to the HTML lang attribute, browser accept headers, 
     * and the customer's supported languages
     * Language is selected from the customer's supported languages in this order: 
     * - exact match to HTML lang attribute
     * - exact match to browser language setting
     * - main 2-letter match to HTML lang attribute
     * - main 2-letter match to browser language setting
     * - customer's default language
     * @param {string} groupId the agent's group ID
     * @param {boolean} staging whether to get the settings from staging instead of production
     */
    private async getLanguage(groupId: string,  baseURL: string, staging: boolean = false) : Promise<string> {
        if (this.language != undefined) {
            return this.language;
        }
        return this.getUISettings(groupId, baseURL, staging)
            .then(settings => {
                const defaultLanguage: string = settings["defaultlanguage"] ? settings["defaultlanguage"] : "en-us";
                const languages: string[] = settings["languages"] ? settings["languages"] : [defaultLanguage];
                const acceptlanguages: string[] = settings["acceptlanguages"] ? settings["acceptlanguages"] : [defaultLanguage];
                this.language = this.getAgentLanguage(languages, acceptlanguages, defaultLanguage);
                console.log(`Language: ${this.language}`);
                return this.language;
            })
    }

    private getPhrasesURL(groupId: string, translationsURL: string, lang: string, staging: boolean=false) {
        const env = staging? "S" : "P";
        return `${translationsURL}/cobrowse/text/${groupId}/VisitorText_${groupId}_${lang}_${env}.json`;
    }

    private async getPhrases(groupId: string, translationsURL: string, lang: string, staging: boolean = false): Promise<PhraseMap> {
        if (this.ready) {
            return this.phrases;
        }
        const result = await this.nativeFetch(this.getPhrasesURL(groupId, translationsURL, lang, staging));
        if (result.ok) {
            this.phrases = (await result.json()) as PhraseMap;
            console.log(`Phrases loaded.`);
        }
        else {
            if (lang.includes('-')) { //If the regional variant cannot be found, default to the base language.
                console.log(`Regional variant ${lang} not found.`);
                const baseLang = lang.split('-')[0];
                console.log(`Setting language to ${baseLang}.`);
                const result2 = await this.nativeFetch(this.getPhrasesURL(groupId, translationsURL, baseLang, staging));
                if (result2.ok) {
                    this.phrases = await result2.json();
                    console.log(`Phrases loaded.`);
                }
                else {
                    throw new Error(`ERR_PHRASES: ${result2.status} ${result2.statusText}`);
                }
            }
            else {
                throw new Error(`ERR_PHRASES: ${result.status} ${result.statusText}`);
            }
        }
        return this.phrases;
    }
}
